import { useTheme } from '@emotion/react';
import { Box, Button, Skeleton, useMediaQuery } from '@mui/material';
import Typography from '@mui/material/Typography';
import { tokens } from '../../global/theme/tokens';
import { generateMediaQuerysForDashboardGridElement } from './dashboardGridManagement';
import { useTranslation } from 'react-i18next';
import { periodStepMap } from './modulePeriodStep';
import { useState } from 'react';

const ModuleLayout = ({
  label,
  children,
  colSpan = 1,
  rowSpan = 1,
  minHeight = 'auto',
  isLoading,
  onPeriodChange,
  periods = [],
  startPeriod,
  noBackground,
  noPadding,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const mediaQuerys = generateMediaQuerysForDashboardGridElement(
    colSpan,
    rowSpan
  );
  return (
    <Box
      className="backdrop-blur-lg rounded-lg flex h-full flex-col overflow-hidden relative"
      sx={{
        minHeight: minHeight,
        backgroundColor: noBackground ? 'transparent' : colors.glass,
        ...mediaQuerys,
      }}
    >
      {isLoading ? (
        <Skeleton
          className="w-full h-full"
          sx={{ transform: 'none', height: '100%' }}
        />
      ) : (
        <>
          <PeriodController
            periods={periods}
            onChange={onPeriodChange}
            startPeriod={startPeriod}
            isMobile={isMobile}
          />
          {label && (
            <Typography
              fontSize={isMobile ? 14 : 16}
              fontWeight={600}
              textAlign="center"
              className="pt-2 opacity-70"
            >
              {label}
            </Typography>
          )}
          <Box
            className="w-full h-full flex flex-col overflow-hidden"
            sx={{
              padding: noPadding ? '0px' : isMobile ? '6px' : '8px',
            }}
          >
            {children}
          </Box>
        </>
      )}
    </Box>
  );
};

const PeriodController = ({ onChange, periods, className, startPeriod, isMobile }) => {
  const [selectedPeriod, setSelectedPeriod] = useState(
    startPeriod || 'quarter'
  );

  function handleChange(newPeriod) {
    setSelectedPeriod(newPeriod);
    onChange(newPeriod);
  }

  return (
    <Box
      className={'flex gap-1 absolute z-30 ' + className}
      sx={{
        right: isMobile ? '4px' : '0',
        bottom: isMobile ? '4px' : '0',
        flexWrap: isMobile ? 'wrap' : 'nowrap',
        maxWidth: isMobile ? 'calc(100% - 8px)' : 'auto',
      }}
    >
      {periods.map((period) => (
        <PeriodButton
          key={period}
          period={period}
          onClick={handleChange}
          selected={selectedPeriod === period}
          isMobile={isMobile}
        />
      ))}
    </Box>
  );
};

const PeriodButton = ({ selected, period, onClick, isMobile }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();

  return (
    <Button
      sx={{
        color: `${colors.grey[800]}${selected ? 'AB' : 70}`,
        bgcolor: selected ? colors.card : 'transparent',
        ':hover': {
          bgcolor: colors.card,
        },
        minWidth: isMobile ? '40px' : 'auto',
        minHeight: isMobile ? '36px' : 'auto',
        padding: isMobile ? '6px 10px' : '6px 8px',
        fontSize: isMobile ? '12px' : '14px',
      }}
      key={period}
      onClick={() => onClick(period)}
    >
      {t(periodStepMap[period].label)}
    </Button>
  );
};

export default ModuleLayout;
